import { computed, inject, Injectable, signal } from '@angular/core'
import { StorageService } from '@/app/core/auth/storage.service'
import { UserRolesEnum } from '@/app/shared/enums/user-roles.enum'
import { UserSession } from '@/app/shared/models/user/user-session.model'

@Injectable({ providedIn: 'root' })
export class PermissionService {
  storageService = inject(StorageService)

  private user = signal<UserSession|null>(this.storageService.getUser());

  loadCurrentUser(): void {
    this.user.set(this.storageService.getUser())
  }

  // Technician
  canAddTechnician = computed(() =>
    this.hasAnyRole([UserRolesEnum.ROLE_RT_MANAGER/*, UserRolesEnum.ADMIN*/]) // todo l'admin doit ajouter une agence
  );

  // Order
  canScheduleOrder = computed(() =>
    this.hasAnyRole([UserRolesEnum.ROLE_RT_MANAGER, UserRolesEnum.ADMIN])
  );

  // Patient
  canValidatePatient = computed(() =>
    this.hasAnyRole([UserRolesEnum.ROLE_PHARMACIST])
  );

  canCheckPendingPatientValidation = computed(() =>
    this.hasAnyRole(
      [
        UserRolesEnum.ROLE_PHARMACIST,
        UserRolesEnum.ROLE_REGIONAL_MANAGER,
        UserRolesEnum.ADMIN,
        UserRolesEnum.ROLE_AGENCY_DIRECTOR,
        UserRolesEnum.ROLE_AVF_REFERENT,
      ])
  );

  canDownloadPatientPrescription = computed(() =>
    this.hasAnyRole(
      [
        UserRolesEnum.ROLE_REGIONAL_MANAGER,
        UserRolesEnum.ADMIN,
        UserRolesEnum.ROLE_AVF_REFERENT,
      ])
  );

  canUpdatePatient = computed(() =>
    this.hasAnyRole(
      [
        UserRolesEnum.ROLE_REGIONAL_MANAGER,
        UserRolesEnum.ADMIN,
        UserRolesEnum.ROLE_AGENCY_DIRECTOR,
        UserRolesEnum.ROLE_AVF_REFERENT,
      ])
  );

  hasAnyRole(allowedRoles: string[]): boolean {
    return this.user()?.roles?.some(role => allowedRoles.includes(role)) ?? false;
  }

  clearUser() {
    this.user.set(null)
  }

  canViewOrders = computed(() =>
    this.hasAnyRole(
      [
        UserRolesEnum.ROLE_REGIONAL_MANAGER,
        UserRolesEnum.ADMIN,
        UserRolesEnum.ROLE_AGENCY_DIRECTOR,
        UserRolesEnum.ROLE_RT_MANAGER,
        UserRolesEnum.ROLE_AVF_REFERENT,
        UserRolesEnum.TECHNICIAN,
       UserRolesEnum.ROLE_PHARMACIST
      ])
  );

  canViewTransfers = computed(() =>
    this.hasAnyRole(
      [
        UserRolesEnum.ROLE_REGIONAL_MANAGER,
        UserRolesEnum.ADMIN,
        UserRolesEnum.ROLE_AGENCY_DIRECTOR,
        UserRolesEnum.ROLE_AVF_REFERENT,
        UserRolesEnum.TECHNICIAN,
        UserRolesEnum.ROLE_PHARMACIST,
        UserRolesEnum.ROLE_RT_MANAGER
      ])
  );

  canViewOrderRetrievals = computed(() =>
    this.hasAnyRole(
      [
        UserRolesEnum.ROLE_REGIONAL_MANAGER,
        UserRolesEnum.ADMIN,
        UserRolesEnum.ROLE_AGENCY_DIRECTOR,
        UserRolesEnum.ROLE_AVF_REFERENT,
        UserRolesEnum.ROLE_RT_MANAGER,
        UserRolesEnum.TECHNICIAN,
        UserRolesEnum.ROLE_PHARMACIST
      ])
  );

  canViewAgencies = computed(() =>
    this.hasAnyRole(
      [
        UserRolesEnum.ROLE_REGIONAL_MANAGER,
        UserRolesEnum.ADMIN,
        UserRolesEnum.ROLE_AGENCY_DIRECTOR,
      ])
  );

  canScheduleOrderWithoutTimeRestrictions = computed(() =>
    this.hasAnyRole(
      [
        UserRolesEnum.ROLE_RT_MANAGER
      ])
  );
}
